/**
 * /dashboard/bulk-imports/$id — Bulk-import detail page (G7.5).
 *
 * Backend: GET /api/v1/bulk-imports/{id}. HR-admin only. Read-only view
 * of one persisted bulk_imports row + per-row outcomes (error_log_jsonb)
 * + a "Download error log CSV" affordance.
 *
 * Migration note (Phase 3f — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/bulk-imports/[id]/page.tsx.
 * Swaps:
 *   - "use client" dropped
 *   - use(params) → Route.useParams()
 *   - next/link Link → @tanstack/react-router Link
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { toApiError } from "@/lib/api";
import { useBulkImport } from "@/hooks/use-bulk-import";
import { PageHeader } from "@/components/shared/page-header";
import { BulkImportRoleGate } from "@/components/bulk-imports/bulk-import-role-gate";
import { BulkImportDetail } from "@/components/bulk-imports/bulk-import-detail";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/bulk-imports/$id")({
  component: BulkImportDetailRoute,
});

function DetailSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-40 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

function BulkImportDetailRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { id } = Route.useParams();
  const numId = Number(id);
  const validId = Number.isFinite(numId) && numId > 0;
  const q = useBulkImport(numId, validId);

  return (
    <BulkImportRoleGate>
      <PageHeader
        // The uploaded file names the import (breadcrumb: Imports › file).
        title={q.data?.file_name || "Bulk Import"}
        description={q.data ? `Import #${q.data.id}` : undefined}
        actions={
          <Button asChild variant="outline" className="gap-1.5 lg:hidden">
            <Link to="/app/$tenantSlug/bulk-imports" params={{ tenantSlug }}>
              <ArrowLeft aria-hidden className="h-4 w-4" />
              Back
            </Link>
          </Button>
        }
      />

      {!validId && (
        <Alert variant="destructive">
          <AlertTitle>Invalid import id</AlertTitle>
          <AlertDescription>
            The URL doesn&apos;t look like a valid import id.
          </AlertDescription>
        </Alert>
      )}

      {validId && q.isLoading && <DetailSkeleton />}

      {validId && q.isError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load this import</AlertTitle>
          <AlertDescription>
            {toApiError(q.error).message}{" "}
            <Link
              to="/app/$tenantSlug/bulk-imports" params={{ tenantSlug }}
              className="ml-1 inline-flex items-center gap-1 font-medium text-primary underline-offset-4 hover:underline"
            >
              Back to Bulk Imports
              <ChevronRight aria-hidden className="h-4 w-4" />
            </Link>
          </AlertDescription>
        </Alert>
      )}

      {validId && q.data && <BulkImportDetail importRow={q.data} />}
    </BulkImportRoleGate>
  );
}
